import { Box, styled, Tooltip } from '@mui/material';

import { CloudProviders } from '@/data/inventory/data';
import type { BackupVault } from '@/data/vaults/backupVault';
import useBackupVaults from '@/data/vaults/useBackupVaults';

import BackupVaultCard from './backupVaultCard';

const Tag = styled('div')(
  (props: { backgroundColor: string; additionalStyles?: object }) => ({
    cursor: 'default',
    borderRadius: '4px',
    fontSize: '12px',
    padding: '2px 8px',
    margin: '2px 4px',
    color: 'black',
    backgroundColor: props.backgroundColor,
    display: 'inline-block',
    ...props.additionalStyles,
  })
);

function VaultTagFromId(props: { vaultId: string }) {
  const { vaults } = useBackupVaults();
  const vault = vaults.find((vault) => vault.id === props.vaultId);
  if (!vault) {
    return null;
  }

  return <VaultTag vault={vault} />;
}

interface VaultTagProps {
  sx?: object;
  vault: BackupVault | string;
}

export default function VaultTag(props: VaultTagProps) {
  if (typeof props.vault === 'string') {
    return <VaultTagFromId vaultId={props.vault} />;
  }

  if (!props.vault) {
    return null;
  }

  return (
    <Tooltip
      disableInteractive={false}
      PopperProps={{
        modifiers: [
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              altAxis: true,
              padding: 8,
            },
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['top', 'bottom', 'left', 'right'],
            },
          },
        ],
      }}
      slotProps={{
        tooltip: {
          sx: {
            padding: 0,
            backgroundColor: 'transparent',
            maxWidth: '300px',
          },
        },
      }}
      title={<BackupVaultCard data={props.vault} editable={false} />}
    >
      <Tag
        data-testid='vault-tag'
        additionalStyles={props.sx}
        backgroundColor={props.vault.backgroundColor || 'black'}
      >
        <Box component={'span'} className='font-semibold mr-[4px]'>
          {
            CloudProviders[props.vault.vaultAttributes.cloudProvider]
              ?.displayName
          }
        </Box>
        <Box component={'span'}>{props.vault.name}</Box>
      </Tag>
    </Tooltip>
  );
}
